/**
 * NDA Template Editor — clause-level editor for a single template
 * version.  Reordering is done with explicit up/down controls (safer
 * than drag-and-drop in the admin context and matches existing REEDOS
 * admin patterns).
 *
 * Editability rules
 * -----------------
 *  * Draft     — every field editable, clauses freely addable/removable.
 *  * Published — editable ONLY if not yet referenced by any NDA.  When
 *                ``in_use`` is true the editor is read-only.
 *  * Retired   — always read-only.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft, Plus, TrashSimple, ArrowUp, ArrowDown, FloppyDisk,
    Sparkle, Prohibit, Copy, Eye, LockKey,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { DialogFooter } from "../../components/ui/dialog";
import FormDialog, { FormDialogCancel } from "../../components/FormDialog";
import PageHeader from "../../components/layout/PageHeader";
import StatusBadge from "../../components/StatusBadge";
import { toast } from "sonner";

const STATUS_BADGE_MAP = {
    "Draft": { status: "Draft" },
    "Published": { status: "Active" },
    "Retired": { status: "Cancelled" },
};

function ClauseDialog({ open, onOpenChange, initial, onSave }) {
    const [heading, setHeading] = useState("");
    const [body, setBody] = useState("");
    const [included, setIncluded] = useState(true);
    useEffect(() => {
        if (!open) return;
        setHeading(initial?.heading || "");
        setBody(initial?.body || "");
        setIncluded(initial?.is_included !== false);
    }, [open, initial]);
    const submit = () => {
        if (!heading.trim()) { toast.error("Heading required"); return; }
        if (!body.trim()) { toast.error("Body required"); return; }
        onSave({ heading: heading.trim(), body: body.trim(), is_included: included });
        onOpenChange(false);
    };
    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title={initial ? "Edit Clause" : "New Clause"}
            testId="nda-template-clause-dialog"
            widthClass="max-w-2xl"
        >
            <div className="space-y-3">
                <div>
                    <Label>Heading *</Label>
                    <Input value={heading} onChange={(e) => setHeading(e.target.value)} data-testid="nda-clause-heading" />
                </div>
                <div>
                    <Label>Body *</Label>
                    <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} data-testid="nda-clause-body" />
                </div>
                <div className="flex items-center gap-2">
                    <input id="incl" type="checkbox" checked={included} onChange={(e) => setIncluded(e.target.checked)} data-testid="nda-clause-included" />
                    <Label htmlFor="incl" className="!mb-0">Include this clause in the rendered NDA</Label>
                </div>
            </div>
            <DialogFooter>
                <FormDialogCancel data-testid="nda-clause-cancel">Cancel</FormDialogCancel>
                <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="nda-clause-save">Save clause</Button>
            </DialogFooter>
        </FormDialog>
    );
}

export default function NDATemplateEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tpl, setTpl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [clauseDialog, setClauseDialog] = useState({ open: false, index: null });

    const load = async () => {
        try {
            const { data } = await api.get(`/billing/nda-template/${id}`);
            setTpl(data);
            setDirty(false);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load template"));
            navigate("/admin/nda-templates");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [id]);

    const editable = useMemo(() => {
        if (!tpl) return false;
        if (tpl.status === "Retired") return false;
        if (tpl.status === "Published" && tpl.in_use) return false;
        return true;
    }, [tpl]);

    if (loading) return <div className="p-8 text-sm text-slate-500">Loading template…</div>;
    if (!tpl) return null;

    const badge = STATUS_BADGE_MAP[tpl.status] || { status: tpl.status };

    const updateField = (patch) => {
        setTpl((t) => ({ ...t, ...patch }));
        setDirty(true);
    };

    const setClauses = (nextClauses) => {
        const ordered = nextClauses.map((c, i) => ({
            heading: c.heading,
            body: c.body,
            is_included: c.is_included !== false,
            display_order: (i + 1) * 10,
        }));
        setTpl((t) => ({ ...t, clauses: ordered }));
        setDirty(true);
    };

    const move = (from, dir) => {
        const to = from + dir;
        if (to < 0 || to >= tpl.clauses.length) return;
        const arr = [...tpl.clauses];
        const [row] = arr.splice(from, 1);
        arr.splice(to, 0, row);
        setClauses(arr);
    };

    const removeClause = (index) => {
        if (!window.confirm("Remove this clause?")) return;
        setClauses(tpl.clauses.filter((_, i) => i !== index));
    };

    const saveClause = (payload) => {
        const { index } = clauseDialog;
        if (index === null) {
            setClauses([...(tpl.clauses || []), payload]);
        } else {
            setClauses(tpl.clauses.map((c, i) => (i === index ? { ...c, ...payload } : c)));
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/billing/nda-template/${tpl.id}`, {
                template_name: tpl.template_name,
                default_purpose: tpl.default_purpose,
                clauses: tpl.clauses,
            });
            setTpl(data);
            setDirty(false);
            toast.success("Saved");
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    const publish = async () => {
        if (!window.confirm(`Publish ${tpl.template_code} v${tpl.version}?  Once used by any NDA it becomes immutable.`)) return;
        try {
            const { data } = await api.post(`/billing/nda-template/${tpl.id}/publish`, { remarks: "" });
            setTpl(data);
            toast.success(`Published v${data.version}`);
        } catch (err) {
            toast.error(formatApiError(err, "Publish failed"));
        }
    };

    const retire = async () => {
        if (!window.confirm(`Retire ${tpl.template_code} v${tpl.version}?  Existing NDAs continue to use their frozen snapshot.`)) return;
        try {
            const { data } = await api.post(`/billing/nda-template/${tpl.id}/retire`, { remarks: "" });
            setTpl(data);
            toast.success(`Retired v${data.version}`);
        } catch (err) {
            toast.error(formatApiError(err, "Retire failed"));
        }
    };

    const clone = async () => {
        try {
            const { data } = await api.post(`/billing/nda-template/${tpl.id}/clone`, {});
            toast.success(`Cloned to v${data.version} (Draft)`);
            navigate(`/admin/nda-templates/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Clone failed"));
        }
    };

    return (
        <div data-testid="nda-template-editor-page">
            <PageHeader
                eyebrow="Admin · NDA Template"
                title={
                    <div className="flex items-center gap-3 flex-wrap">
                        <Scales title="template" />
                        <span className="font-mono">{tpl.template_code}</span>
                        <span className="text-slate-500">v{tpl.version}</span>
                        <StatusBadge {...badge}>{tpl.status}</StatusBadge>
                        {tpl.in_use && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"><LockKey size={10} /> In use</span>}
                    </div>
                }
                description={`${tpl.nda_type} NDA · ${tpl.template_name}`}
                actions={
                    <div className="flex flex-wrap items-center gap-2" data-testid="nda-template-editor-actions">
                        <Button variant="outline" onClick={() => navigate("/admin/nda-templates")} className="gap-1.5" data-testid="nda-template-back">
                            <ArrowLeft size={14} /> Back
                        </Button>
                        <Button variant="outline" onClick={() => window.open(`/admin/nda-templates/${tpl.id}/preview`, "_blank", "noopener")} className="gap-1.5" data-testid="nda-template-preview">
                            <Eye size={14} /> Preview
                        </Button>
                        <Button variant="outline" onClick={clone} className="gap-1.5" data-testid="nda-template-editor-clone">
                            <Copy size={14} /> Clone as v{Number(tpl.version) + 1}
                        </Button>
                        {editable && dirty && (
                            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5" data-testid="nda-template-save">
                                <FloppyDisk size={14} /> {saving ? "Saving…" : "Save Draft"}
                            </Button>
                        )}
                        {tpl.status === "Draft" && (
                            <Button onClick={publish} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5" data-testid="nda-template-editor-publish">
                                <Sparkle size={14} /> Publish
                            </Button>
                        )}
                        {tpl.status !== "Retired" && (
                            <Button variant="outline" onClick={retire} className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="nda-template-editor-retire">
                                <Prohibit size={14} /> Retire
                            </Button>
                        )}
                    </div>
                }
            />
            <div className="px-6 sm:px-8 py-8 space-y-6">
                {!editable && (
                    <div className="reedos-card p-3 bg-slate-50 border-slate-200 text-xs text-slate-700 flex items-start gap-2" data-testid="nda-template-readonly-banner">
                        <LockKey size={14} className="mt-0.5 text-slate-500" />
                        <div>
                            {tpl.status === "Retired"
                                ? "This template is Retired.  Historical NDAs continue to reference its frozen snapshot; no editing is permitted."
                                : "Published templates that have been referenced by an NDA are immutable.  Clone as a new version to evolve the legal wording."}
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="reedos-card p-5" data-testid="nda-template-header-card">
                    <div className="reedos-eyebrow mb-3">Template Header</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                            <Label>Template Code</Label>
                            <div className="mt-1 font-mono text-sm text-slate-900">{tpl.template_code}</div>
                        </div>
                        <div>
                            <Label>Version</Label>
                            <div className="mt-1 text-sm text-slate-900">v{tpl.version}</div>
                        </div>
                        <div>
                            <Label>NDA Type</Label>
                            <div className="mt-1 text-sm text-slate-900">{tpl.nda_type} NDA</div>
                        </div>
                        <div className="md:col-span-2">
                            <Label>Template Name</Label>
                            {editable ? (
                                <Input value={tpl.template_name} onChange={(e) => updateField({ template_name: e.target.value })} data-testid="nda-template-name-input" />
                            ) : <div className="mt-1 text-sm">{tpl.template_name}</div>}
                        </div>
                        <div>
                            <Label>Published</Label>
                            <div className="mt-1 text-xs text-slate-600">{tpl.published_at ? formatDate(tpl.published_at) : "—"}</div>
                        </div>
                        <div className="md:col-span-3">
                            <Label>Default Purpose</Label>
                            {editable ? (
                                <Textarea rows={2} value={tpl.default_purpose || ""} onChange={(e) => updateField({ default_purpose: e.target.value })} data-testid="nda-template-purpose-input" />
                            ) : <div className="mt-1 text-sm text-slate-700 whitespace-pre-line">{tpl.default_purpose}</div>}
                        </div>
                    </div>
                </div>

                {/* Clauses */}
                <div className="reedos-card p-5" data-testid="nda-template-clauses-card">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="reedos-eyebrow">Clauses</div>
                            <div className="text-xs text-slate-500">Ordered top-to-bottom.  {editable ? "Use up/down arrows to reorder." : "Read-only view."}</div>
                        </div>
                        {editable && (
                            <Button onClick={() => setClauseDialog({ open: true, index: null })} className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5" data-testid="nda-template-add-clause">
                                <Plus size={14} /> Add Clause
                            </Button>
                        )}
                    </div>
                    <ol className="space-y-2">
                        {(tpl.clauses || []).map((c, idx) => (
                            <li key={idx} className={`border rounded-md p-3 ${c.is_included === false ? "opacity-50 border-slate-200 bg-slate-50" : "border-slate-200"}`} data-testid={`nda-template-clause-${idx}`}>
                                <div className="flex items-start gap-3">
                                    <div className="text-xs text-slate-500 font-mono pt-1 w-6 text-right">{idx + 1}.</div>
                                    <div className="flex-1">
                                        <div className="font-semibold text-sm text-slate-900">{c.heading}</div>
                                        <div className="text-xs text-slate-700 mt-1 whitespace-pre-line">{c.body}</div>
                                        {c.is_included === false && (
                                            <span className="inline-block text-[10px] uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-2">Excluded from rendered NDA</span>
                                        )}
                                    </div>
                                    {editable && (
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30" data-testid={`nda-clause-up-${idx}`} title="Move up">
                                                <ArrowUp size={13} />
                                            </button>
                                            <button onClick={() => move(idx, 1)} disabled={idx === (tpl.clauses || []).length - 1} className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30" data-testid={`nda-clause-down-${idx}`} title="Move down">
                                                <ArrowDown size={13} />
                                            </button>
                                            <button onClick={() => setClauseDialog({ open: true, index: idx })} className="p-1 text-slate-700 hover:text-slate-900" data-testid={`nda-clause-edit-${idx}`} title="Edit">
                                                <Sparkle size={13} />
                                            </button>
                                            <button onClick={() => removeClause(idx)} className="p-1 text-rose-600 hover:text-rose-800" data-testid={`nda-clause-remove-${idx}`} title="Remove">
                                                <TrashSimple size={13} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                        {(tpl.clauses || []).length === 0 && (
                            <li className="text-center text-sm text-slate-500 py-6" data-testid="nda-template-clauses-empty">
                                No clauses yet.  Add at least one clause before publishing.
                            </li>
                        )}
                    </ol>
                </div>
            </div>

            <ClauseDialog
                open={clauseDialog.open}
                onOpenChange={(o) => setClauseDialog((c) => ({ ...c, open: o }))}
                initial={clauseDialog.index !== null ? tpl.clauses?.[clauseDialog.index] : null}
                onSave={saveClause}
            />
        </div>
    );
}

// Tiny inline icon so we don't need another dependency for the header title.
function Scales() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M12 4v16m-6-6a4 4 0 108 0M6 14l-3-7m3 7l3-7M18 14a4 4 0 108 0m-8 0l3-7m-3 7l-3-7M5 21h14" stroke="#334155" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
